import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { FileDescriptorRead } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";

import {
  DescriptorViewType,
  ViewTypesArgs,
  ViewTypesMap,
  viewToDataTypeMap,
} from "@/types/descriptor-types";
import { descriptorIsLoading } from "@/utils/descriptor-is-loading";
import { isTypedDescriptor } from "@/utils/is-descriptor-type";

import { updateItemInQuery } from "./hooks/create-item-updater-for-query";
import { queryDirectly } from "./hooks/query-directly";
import { QueryReturn } from "./types/query-return";

// Single (descriptor self)

export function getFileDescriptorQuery(descriptorId: string | undefined) {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["file-descriptor", descriptorId],
    queryFn: async () => {
      return await getRanaApiV1().FileDescriptorsApi.fileDescriptorRetrieve({
        fileDescriptorId: descriptorId + "",
      });
    },
    refetchInterval: (query) => (descriptorIsLoading(query.state.data) ? 5000 : false),
    enabled: !!descriptorId,
  });
}

export const useFileDescriptorQuery = (descriptorId: string | undefined) => {
  return useQuery(getFileDescriptorQuery(descriptorId));
};

export const useFileDescriptorsQuery = (descriptorIds: string[]) => {
  return useQueries({
    queries: descriptorIds.map((descriptorId) => getFileDescriptorQuery(descriptorId)),
  });
};

export const useFileDescriptorQueryDirectly = () => {
  const queryClient = useQueryClient();
  return async (descriptorId: string) =>
    queryDirectly(queryClient, getFileDescriptorQuery(descriptorId));
};

// INVALIDATION (descriptor self)

export const useInvalidateFileDescriptorQuery = () => {
  const queryClient = useQueryClient();
  return async (descriptorId: string | undefined) => {
    await queryClient.invalidateQueries({
      queryKey: getFileDescriptorQuery(descriptorId).queryKey,
      refetchType: "all",
    });
  };
};

type FileDescriptorQueryReturn = QueryReturn<typeof getFileDescriptorQuery>;

export const useUpdateFileDescriptorQuery = () => {
  const queryClient = useQueryClient();
  return updateItemInQuery<FileDescriptorQueryReturn>(queryClient, {
    getQueryKey: (id) => getFileDescriptorQuery(id).queryKey,
  });
};

// Single (descriptor view)

// Some view types require query params, in those case we want to require to pass them. And if they are not required, we don't want to require passing anything. Thats what this type helps us accomplish
type ViewTypeArgsTuple<T extends DescriptorViewType> = ViewTypesArgs[T] extends undefined
  ? []
  : [args: ViewTypesArgs[T]];

export const getFileDescriptorViewQuery = <T extends DescriptorViewType>(
  descriptor: FileDescriptorRead | undefined,
  viewType: T,
  viewTypeArgs?: ViewTypesArgs[T]
) => {
  const descriptorId = descriptor?.id;
  const status = descriptor?.status?.id;

  // See which data types are supported for this view type, and only enable the query if the descriptor is of one of those types.
  const dataTypes = viewToDataTypeMap[viewType];

  if (descriptor?.dataType && !(dataTypes as string[]).includes(descriptor?.dataType)) {
    console.warn(
      `Descriptor's data type is ${
        descriptor?.dataType
      }, but it is not supported for view type ${viewType}, which only has the supported data types ${dataTypes.join(
        ", "
      )}. The query will be disabled.`
    );
  }

  return getRanaApiV1().getQueryOptions<ViewTypesMap[T]>({
    queryKey: ["file-descriptor", viewType, descriptorId, status, viewTypeArgs],
    queryFn: async () => {
      // Some of the descriptor view types require query params. But the API client doesn't support them, so we have to do a manual fetch here.
      // We do still get the path from the API client, so we don't have to hardcode it here.
      const requestOptions = await getRanaApiV1().FileDescriptorsApi.fileDescriptorViewRequestOpts({
        viewType,
        fileDescriptorId: descriptorId + "",
      });

      const queryParams = viewTypeArgs ? new URLSearchParams(viewTypeArgs).toString() : undefined;

      const response = await getRanaApiV1().BaseApi.fetch(
        `${requestOptions.path}${queryParams ? `?${queryParams}` : ""}`
      );

      return await response.json();
    },
    enabled:
      !!descriptorId && dataTypes.some((dataType) => isTypedDescriptor(descriptor, dataType)),
  });
};

export const useFileDescriptorViewQuery = <T extends DescriptorViewType>(
  descriptorId: string | undefined,
  viewType: T,
  ...args: ViewTypeArgsTuple<T>
) => {
  const descriptorQuery = useFileDescriptorQuery(descriptorId);
  return useQuery(getFileDescriptorViewQuery(descriptorQuery.data ?? undefined, viewType, ...args));
};

export const useFileDescriptorViewsQuery = <T extends DescriptorViewType>(
  descriptorIds: string[],
  viewType: T,
  ...args: ViewTypeArgsTuple<T>
) => {
  const descriptorQueries = useFileDescriptorsQuery(descriptorIds);
  return useQueries({
    queries: descriptorQueries.map(({ data }) =>
      getFileDescriptorViewQuery<T>(data ?? undefined, viewType, ...args)
    ),
  });
};

// INVALIDATION (descriptor view)

export const useInvalidateFileDescriptorViewAsQuery = () => {
  const queryClient = useQueryClient();
  return async (descriptorId: string | undefined, dataType: DescriptorViewType) => {
    const descriptor = queryClient.getQueryData<FileDescriptorRead>(
      getFileDescriptorQuery(descriptorId).queryKey
    );
    await queryClient.invalidateQueries({
      queryKey: getFileDescriptorViewQuery(descriptor, dataType).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};
